import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { ClassTimetableGrid, ExamEvent, PeriodSlot, WEEKDAYS, Weekday } from '../models/schedule.model';
import { LocalStorageService } from '../services/local-storage.service';
import { ScheduleService } from '../services/schedule.service';
import { CLASS_GROUP_OPTIONS } from '../school-constants';

const DAY_LABEL: Record<Weekday, string> = {
  MON: 'Mon',
  TUE: 'Tue',
  WED: 'Wed',
  THU: 'Thu',
  FRI: 'Fri',
  SAT: 'Sat'
};

@Component({
  selector: 'app-schedules',
  templateUrl: './schedules.component.html',
  styleUrls: ['./schedules.component.css']
})
export class SchedulesComponent implements OnInit {

  readonly classOptions = [...CLASS_GROUP_OPTIONS];
  readonly days: Weekday[] = [...WEEKDAYS];

  isAdmin = false;
  periods: PeriodSlot[] = [];
  selectedClass = '10';
  grid: ClassTimetableGrid | null = null;
  loading = false;
  savingPeriods = false;
  savingGrid = false;

  exams: ExamEvent[] = [];
  examColumns = ['title', 'className', 'date', 'time', 'type', 'actions'];
  examData = new MatTableDataSource<ExamEvent>([]);
  examForm: FormGroup;

  constructor(
    private scheduleService: ScheduleService,
    private localStorage: LocalStorageService,
    private snackBar: MatSnackBar,
    private fb: FormBuilder
  ) {
    this.examForm = this.fb.group({
      title: ['', Validators.required],
      className: ['10', Validators.required],
      date: [new Date(), Validators.required],
      startTime: ['09:00', Validators.required],
      endTime: ['11:00', Validators.required],
      type: ['EXAM', Validators.required]
    });
  }

  ngOnInit(): void {
    this.isAdmin = this.localStorage.isAdmin();
    this.refreshPeriodsAndGrid();
    this.loadExams();
  }

  dayLabel(d: Weekday): string {
    return DAY_LABEL[d];
  }

  refreshPeriodsAndGrid(): void {
    this.loading = true;
    this.scheduleService.getPeriods().subscribe(
      p => {
        this.periods = p && p.length ? p : this.scheduleService.defaultPeriods();
        this.loadGridForClass();
        this.loading = false;
      },
      () => {
        this.periods = this.scheduleService.defaultPeriods();
        this.loadGridForClass();
        this.loading = false;
      }
    );
  }

  onClassChange(): void {
    this.loadGridForClass();
  }

  loadGridForClass(): void {
    const n = this.periods.length;
    if (!n) {
      return;
    }
    this.scheduleService.getTimetableGrid(this.selectedClass, n).subscribe(g => {
      this.grid = g;
    });
  }

  savePeriods(): void {
    if (!this.isAdmin) {
      return;
    }
    this.savingPeriods = true;
    this.scheduleService.savePeriods(this.periods).subscribe(
      () => {
        this.savingPeriods = false;
        this.snackBar.open('Period times saved (and cached in this browser).', 'Dismiss', { duration: 4000 });
        this.loadGridForClass();
      },
      () => {
        this.savingPeriods = false;
        this.snackBar.open('Saved locally; server PUT /schedules/periods may be unavailable.', 'Dismiss', { duration: 5000 });
      }
    );
  }

  saveTimetable(): void {
    if (!this.isAdmin || !this.grid) {
      return;
    }
    this.savingGrid = true;
    this.scheduleService.saveTimetableGrid(this.grid, this.periods.length).subscribe(
      () => {
        this.savingGrid = false;
        this.snackBar.open('Timetable saved for ' + this.selectedClass, 'Dismiss', { duration: 4000 });
      },
      () => {
        this.savingGrid = false;
        this.snackBar.open('Saved locally; server PUT may be unavailable.', 'Dismiss', { duration: 5000 });
      }
    );
  }

  loadExams(): void {
    this.scheduleService.getExams().subscribe(list => {
      this.exams = list || [];
      this.examData.data = this.exams;
    });
  }

  addExam(): void {
    if (!this.isAdmin || this.examForm.invalid) {
      return;
    }
    const v = this.examForm.value;
    const d = v.date instanceof Date ? this.formatDate(v.date) : String(v.date);
    const row: ExamEvent = {
      id: 'ex-' + Date.now(),
      title: v.title.trim(),
      className: v.className,
      date: d,
      startTime: v.startTime,
      endTime: v.endTime,
      type: v.type
    };
    const next = [...this.exams, row];
    this.scheduleService.saveExams(next).subscribe(
      () => {
        this.exams = next;
        this.examData.data = next;
        this.examForm.patchValue({ title: '' });
        this.snackBar.open('Exam / test added', 'Dismiss', { duration: 3000 });
      },
      () => {
        this.snackBar.open('Saved locally.', 'Dismiss', { duration: 3000 });
        this.exams = next;
        this.examData.data = next;
      }
    );
  }

  deleteExam(row: ExamEvent): void {
    if (!this.isAdmin) {
      return;
    }
    const next = this.exams.filter(e => e.id !== row.id);
    this.scheduleService.saveExams(next).subscribe(
      () => {
        this.exams = next;
        this.examData.data = next;
      },
      () => {
        this.exams = next;
        this.examData.data = next;
      }
    );
  }

  formatDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
}
